import { notificationMiddleware } from '../../../store/middleware';
import { unlockRoom, lockRoom, resendBookingInvitation } from '../../Map/store/RoomMenu/RoomMenuThunks';
import { addNotification } from './notificationsSlice';
import { MESSAGE_SUCCESS, RECEIVED_ROOM_MESSAGE, RECIEVE_MESSAGE } from '../../../store/messagesSlice/messagesSlice';
import { sendTest } from '../../../store/socketSlice/socketSlice';
import socketFactory, { mapSocketFactory } from '../../../utils/socketFactory.js';
import { sendEmail, addGuest, resendInvitation } from '../../ControlPanel/store/Invite/inviteThunk';
import { updateBooking, deleteBooking, createBooking, createConference } from '../../ControlPanel/store/AddEvent/AddEventThunks';
import { sendRemeinder } from '../../ControlPanel/store/Conference/ConferenceThunks.js';
import { deleteBackground, deleteDefaultBackground } from '../../ControlPanel/store/settings/settingsThunks';
import { occupyChair } from '../../Map/store/Chairs/ChairsThunk.js';
import { POKE_USER } from '../../Map/store/Avatars/AvatarsSlice.js';
import { ON_POKE, MIC_CLOSED, MIC_STATE_CONFIRMATION, ON_EXCLUDE_USER } from '../../MediaControls/store/mediaControlSlice.js';
import { transcriptionStarted, transcriptionStopped } from "../../MediaPlayback/store/TranscriptionSlice/TranscriptionSlice";
import { sendWebPushNotification } from '../../../utils/sendWebPushNotification';
import i18n from "../../../i18n";
import { uploadBackground, uploadDefaultBackground } from '../../ControlPanel/store/settings/settingsThunks.js';
import { deletePhoto, updatePhoto } from '../../ControlPanel/store/controlPanelThunks.js';
import { apiThunkForSupportModal } from '../../ControlPanel/store/apiThunkForSupportModal.js';
import { virerUser } from '../../Map/store/MapDisplayThunks.js';
import { copyRoomLink } from '../../Map/store/RoomMenu/RoomMenuSlice.js';
import { joinRoom } from '../../ControlPanel/store/interact/interactThunks.js';
import NotificationBuilder from '../utils/NotificationBuilder.js';
import { AddUser, DeleteUser, UpdateUser } from '../../Administration/store/adminThunks.js';

// ---------------------------------------------------------------------------
// CONVENTION (applies to every listener below):
//
//   addNotification(...) only ever receives translation KEYS via the
//   builder's withHeader()/withMessage() — never resolved text. The UI
//   layer is responsible for resolving keys + variables at render time.
//
//   Anything that leaves this module by another path (sendWebPushNotification,
//   console logs, etc.) is NOT a Redux-rendered notification, so it gets
//   PRE-TRANSLATED text via i18n.t() before being sent — those consumers
//   have no key-resolution step of their own.
// ---------------------------------------------------------------------------

// -------------------------------------------------------------------
// Socket event handlers
// -------------------------------------------------------------------
socketFactory.on('announcementNotification', (data) => {
  const payload = data?.data || data;
  const notification = NotificationBuilder.info()
    .withId('announcement_in_progress') // id stable, ciblable au stop
    .withHeader('announcement_in_progress', true)
    .withMessage('announcement_in_progress_msg', true, { name: payload?.announcerName || 'Someone' })
    .withLucideIcon('megaphone')
    .withOptions({ permanent: true })
    .build();
  return { type: 'notifications/addNotification', payload: notification };
});
socketFactory.on('stopannouncementNotification', () => {
  return { type: 'notifications/removeNotification', payload: 'announcement_in_progress' };
});


mapSocketFactory.on('onRoomUpdated', (data) => {
  console.log("onRoomUpdated")
  const notification = NotificationBuilder.success()
    .withHeader('update_room', true)
    .withMessage('update_room_msg', true)
    .build();
  return { type: 'notifications/addNotification', payload: notification };
});




socketFactory.on('conferenceReminder', (data) => {
  const notification = NotificationBuilder.info()
    .withHeader('conference_upcoming', true)
    .withMessage('conference_upcoming_msg', true)
    .withLucideIcon('video')
    .build();
  return { type: addNotification.type, payload: notification };
});

socketFactory.on('meetingReminder', (data) => {
  const payload = data?.data || data;

  // Push notification: pre-translated, since the push layer can't resolve keys.
  const pushTitle = i18n.t('meeting_reminder');
  const pushBody = i18n.t('meeting_reminder_msg', {
    title: payload.title,
    minutes: payload.reminder_duration
  });
  sendWebPushNotification(pushTitle, pushBody);

  const notification = NotificationBuilder.info()
    .withHeader('meeting_reminder', true)
    .withMessage('meeting_reminder_msg', true, {
      title: payload.title,
      minutes: payload.reminder_duration
    })
    .withLucideIcon('calendar-clock')
    .build();
  return { type: addNotification.type, payload: notification };
});

socketFactory.on('lockedRoomAlert', (data) => {
  const payload = data?.data || data;
  const notification = NotificationBuilder.error()
    .withHeader('room_access_attempt', true)
    .withMessage('room_access_attempt_msg', true, { name: payload?.display_name })
    .withLucideIcon('shield')
    .build();
  return { type: addNotification.type, payload: notification };
});

socketFactory.on('conferenceRoomClear', (data) => {
  const notification = NotificationBuilder.error()
    .withHeader('conference_room_clear', true)
    .withMessage('conference_room_clear_msg', true)
    .build();
  return { type: addNotification.type, payload: notification };
});

// -------------------------------------------------------------------
// Redux middleware listeners
// -------------------------------------------------------------------
notificationMiddleware.startListening({
  actionCreator: unlockRoom.fulfilled,
  effect: async (_, listenerApi) => {
    listenerApi.dispatch(addNotification(
      NotificationBuilder.success()
        .withHeader('room_unlocked', true)
        .withMessage('room_unlocked_open_entry_msg', true)
        .withSound('unlock.wav')
        .build()
    ));
  },
});

notificationMiddleware.startListening({
  actionCreator: lockRoom.fulfilled,
  effect: async (_, listenerApi) => {
    listenerApi.dispatch(addNotification(
      NotificationBuilder.error()
        .withHeader('room_locked', true)
        .withMessage('room_locked_no_entry_msg', true)
        .withSound('lock.wav')
        .build()
    ));
  },
});

notificationMiddleware.startListening({
  actionCreator: POKE_USER,
  effect: async (action, listenerApi) => {
    const targetFullName = action.payload.data.targetFullName;
    listenerApi.dispatch(addNotification(
      NotificationBuilder.success()
        .withHeader('poke_sent', true)
        .withMessage('poke_sent_msg', true, { name: targetFullName })
        .build()
    ));
  },
});

notificationMiddleware.startListening({
  actionCreator: MIC_CLOSED,
  effect: async (action, listenerApi) => {
    const sender = action.payload.data.sender;
    listenerApi.dispatch(addNotification(
      NotificationBuilder.info()
        .withHeader('mic_muted_by', true)
        .withMessage('mic_muted_by_other_msg', true, { name: sender })
        .withLucideIcon('mic-off')
        .build()
    ));
  },
});

notificationMiddleware.startListening({
  actionCreator: MIC_STATE_CONFIRMATION,
  effect: async (action, listenerApi) => {
    const target = action.payload.data.target;
    listenerApi.dispatch(addNotification(
      NotificationBuilder.info()
        .withHeader('mic_disabled', true)
        .withMessage('mic_disabled_by_you_msg', true, { name: target })
        .withLucideIcon('mic-off')
        .build()
    ));
  },
});

notificationMiddleware.startListening({
  actionCreator: ON_POKE,
  effect: async (action, listenerApi) => {
    const sender = action.payload.data.sender;
    console.log("action.payload", action.payload);
    const senderName = sender  || 'someone';

    listenerApi.dispatch(addNotification(
      NotificationBuilder.success()
        .withHeader('poke_received', true)
        .withMessage('poke_received_msg', true, { name: senderName })
        .withSound('bell-ring.mp3')
        .build()
    ));

    // Push notification: pre-translated, as before.
    const pushTitle = i18n.t('poke_received');
    const pushBody = i18n.t('poke_received_msg', { name: senderName });
    sendWebPushNotification(pushTitle, pushBody);
  },
});
/*
notificationMiddleware.startListening({
  actionCreator: MESSAGE_SUCCESS,
  effect: async (action, listenerApi) => {
    const state = listenerApi.getState();
    const data = action?.payload?.data ?? action?.payload ?? {};
    const success = data?.success === true;

    if (!success) {
      listenerApi.dispatch(addNotification(
        NotificationBuilder.error()
          .withHeader('message_not_sent', true)
          .withMessage('message_not_sent_default_msg', true)
          .withOptions(data?.error ? { detail: data.error } : {})
          .build()
      ));
      return;
    }

    if (data?.scope === 'room') {
      const roomLabel =
        data?.roomLabel ||
        state.messageHistory?.pendingRoom?.label ||
        state.messageHistory?.target?.label ||
        data?.roomId;

      listenerApi.dispatch(addNotification(
        NotificationBuilder.success()
          .withHeader('message_sent', true)
          .withMessage('message_sent_room_msg', true, { roomLabel })
          .build()
      ));
      return;
    }

    if (data?.scope === 'dm') {
      const recipient = data?.recipient;
      const recipientName =
        recipient?.display_name || recipient?.name || recipient?.email || 'someone';
      listenerApi.dispatch(addNotification(
        NotificationBuilder.success()
          .withHeader('message_sent', true)
          .withMessage('message_sent_direct_msg', true, { name: recipientName })
          .withLucideIcon('message-circle')
          .build()
      ));
      return;
    }
  },
});
*/
notificationMiddleware.startListening({
  actionCreator: joinRoom.rejected,
  effect: async (action, listenerApi) => {
    const payloadString = action.payload;
    if (payloadString && typeof payloadString === 'string' && payloadString.includes('ROOM_FULL')) {
      const match = payloadString.match(/room:\s*(.*)/i);
      const roomLabel = match ? match[1].trim() : '';
      listenerApi.dispatch(addNotification(
        NotificationBuilder.error()
          .withHeader('room_full', true)
          .withMessage('room_full_msg', true, { roomLabel })
          .build()
      ));
    }
  },
});

notificationMiddleware.startListening({
  actionCreator: sendEmail.fulfilled,
  effect: async (_, listenerApi) => {
    listenerApi.dispatch(addNotification(
      NotificationBuilder.success()
        .withHeader('email_sent', true)
        .withMessage('email_sent_msg', true)
        .withLucideIcon('mail-check')
        .build()
    ));
  },
});

notificationMiddleware.startListening({
  actionCreator: sendEmail.rejected,
  effect: async (_, listenerApi) => {
    listenerApi.dispatch(addNotification(
      NotificationBuilder.error()
        .withHeader('error_sending_email', true)
        .withMessage('error_sending_email_msg', true)
        .withLucideIcon('mail-x')
        .build()
    ));
  },
});
notificationMiddleware.startListening({
  actionCreator: DeleteUser.rejected,
  effect: async (_, listenerApi) => {
    listenerApi.dispatch(addNotification(
      NotificationBuilder.error()
        .withHeader('error_delete_user', true)
        .withMessage('error_delete_usermsg', true)
        .withLucideIcon('trash-2')
        .build()
    ));
  },
});
notificationMiddleware.startListening({
  actionCreator: DeleteUser.fulfilled,
  effect: async (_, listenerApi) => {
    listenerApi.dispatch(addNotification(
      NotificationBuilder.success()
        .withHeader('delete_user', true)
        .withMessage('delete_user_msg', true)
        .withLucideIcon('trash-2')
        .build()
    ));
  },
});
notificationMiddleware.startListening({
  actionCreator: AddUser.rejected,
  effect: async (_, listenerApi) => {
    listenerApi.dispatch(addNotification(
      NotificationBuilder.error()
        .withHeader('error_add_user', true)
        .withMessage('error_add_usermsg', true)
        .withLucideIcon('user-plus')
        .build()
    ));
  },
});
notificationMiddleware.startListening({
  actionCreator: AddUser.fulfilled,
  effect: async (_, listenerApi) => {
    listenerApi.dispatch(addNotification(
      NotificationBuilder.success()
        .withHeader('add_user_notif', true)
        .withMessage('add_user_msg', true)
        .withLucideIcon('user-plus')
        .build()
    ));
  },
});
notificationMiddleware.startListening({
  actionCreator: UpdateUser.rejected,
  effect: async (_, listenerApi) => {
    listenerApi.dispatch(addNotification(
      NotificationBuilder.error()
        .withHeader('error_update_user', true)
        .withMessage('error_update_user_msg', true)
        .withLucideIcon('pencil')
        .build()
    ));
  },
});
notificationMiddleware.startListening({
  actionCreator: UpdateUser.fulfilled,
  effect: async (_, listenerApi) => {
    listenerApi.dispatch(addNotification(
      NotificationBuilder.success()
        .withHeader('update_user', true)
        .withMessage('update_user_msg', true)
        .withLucideIcon('pencil')
        .build()
    ));
  },
});
notificationMiddleware.startListening({
  actionCreator: resendInvitation.fulfilled,
  effect: async (_, listenerApi) => {
    listenerApi.dispatch(addNotification(
      NotificationBuilder.success()
        .withHeader('invitation_resent', true)
        .withMessage('invitation_resent_msg', true)
        .build()
    ));
  },
});

notificationMiddleware.startListening({
  actionCreator: resendInvitation.rejected,
  effect: async (_, listenerApi) => {
    listenerApi.dispatch(addNotification(
      NotificationBuilder.error()
        .withHeader('resend_failed', true)
        .withMessage('resend_failed_msg', true)
        .build()
    ));
  },
});

notificationMiddleware.startListening({
  actionCreator: resendBookingInvitation.fulfilled,
  effect: async (_, api) => {
    api.dispatch(addNotification(
      NotificationBuilder.success()
        .withHeader('reminder_sent', true)
        .withMessage('reminder_sent_msg', true)
        .withLucideIcon('send')
        .build()
    ));
  },
});

notificationMiddleware.startListening({
  actionCreator: resendBookingInvitation.rejected,
  effect: async (_, api) => {
    api.dispatch(addNotification(
      NotificationBuilder.error()
        .withHeader('resend_failed', true)
        .withMessage('resend_failed_msg', true)
        .build()
    ));
  },
});

notificationMiddleware.startListening({
  actionCreator: addGuest.fulfilled,
  effect: async (action, listenerApi) => {
    const sendEmailFlag = action.meta.arg?.body?.sendEmail;
    const anonymous = action.meta.arg?.body?.anonymous;

    if (sendEmailFlag) {
      listenerApi.dispatch(addNotification(
        NotificationBuilder.success()
          .withHeader('guest_added', true)
          .withMessage('guest_added_msg', true)
          .withLucideIcon('user-round-plus')
          .build()
      ));
    } else if (anonymous) {
      listenerApi.dispatch(addNotification(
        NotificationBuilder.info()
          .withHeader('link_generated', true)
          .withMessage('link_generated_anonymous_msg', true)
          .withLucideIcon('link')
          .build()
      ));
    } else {
      listenerApi.dispatch(addNotification(
        NotificationBuilder.success()
          .withHeader('link_generated', true)
          .withMessage('link_generated_success_msg', true)
          .build()
      ));
    }
  },
});

notificationMiddleware.startListening({
  actionCreator: addGuest.rejected,
  effect: async (action, listenerApi) => {
    if (action.payload === 'ALREADY_A_USER') {
      listenerApi.dispatch(addNotification(
        NotificationBuilder.error()
          .withHeader('guest_already_present', true)
          .withMessage('guest_already_present_msg', true)
          .build()
      ));
    } else if (action.payload === 'ALREADY_A_GUEST_USER') {
      listenerApi.dispatch(addNotification(
        NotificationBuilder.error()
          .withHeader('guest_already_added', true)
          .withMessage('guest_already_added_msg', true)
          .build()
      ));
    } else {
      listenerApi.dispatch(addNotification(
        NotificationBuilder.error()
          .withHeader('error_adding_guest', true)
          .withMessage('error_adding_guest_msg', true)
          .withLucideIcon('user-round-x')
          .build()
      ));
    }
  },
});

notificationMiddleware.startListening({
  actionCreator: sendTest,
  effect: async () => {
    console.log('Test action intercepted by middleware');
  },
});

notificationMiddleware.startListening({
  actionCreator: updateBooking.fulfilled,
  effect: async (_, listenerApi) => {
    listenerApi.dispatch(addNotification(
      NotificationBuilder.success()
        .withHeader('reservation_updated', true)
        .withMessage('reservation_updated_msg', true)
        .withLucideIcon('calendar-check')
        .build()
    ));
  },
});

notificationMiddleware.startListening({
  actionCreator: deleteBooking.fulfilled,
  effect: async (_, listenerApi) => {
    listenerApi.dispatch(addNotification(
      NotificationBuilder.success()
        .withHeader('reservation_deleted', true)
        .withMessage('reservation_deleted_msg', true)
        .build()
    ));
  },
});

notificationMiddleware.startListening({
  actionCreator: deleteBackground.fulfilled,
  effect: async (_, listenerApi) => {
    listenerApi.dispatch(addNotification(
      NotificationBuilder.success()
        .withHeader('background_deleted', true)
        .withMessage('background_deleted_msg', true)
        .build()
    ));
  },
});

notificationMiddleware.startListening({
  actionCreator: deleteBackground.rejected,
  effect: async (_, listenerApi) => {
    listenerApi.dispatch(addNotification(
      NotificationBuilder.error()
        .withHeader('background_delete_error', true)
        .withMessage('background_delete_error_msg', true)
        .build()
    ));
  },
});

notificationMiddleware.startListening({
  actionCreator: createBooking.fulfilled,
  effect: async (_, listenerApi) => {
    listenerApi.dispatch(addNotification(
      NotificationBuilder.success()
        .withHeader('event_created', true)
        .withMessage('event_created_msg', true)
        .build()
    ));
  },
});

notificationMiddleware.startListening({
  actionCreator: createConference.fulfilled,
  effect: async (_, listenerApi) => {
    listenerApi.dispatch(addNotification(
      NotificationBuilder.success()
        .withHeader('conference_created', true)
        .withMessage('conference_created_msg', true)
        .withLucideIcon('video')
        .build()
    ));
  },
});

notificationMiddleware.startListening({
  actionCreator: sendRemeinder.fulfilled,
  effect: async (_, listenerApi) => {
    listenerApi.dispatch(addNotification(
      NotificationBuilder.success()
        .withHeader('reminder_sent', true)
        .withMessage('reminder_sent_msg', true)
        .build()
    ));
  },
});

notificationMiddleware.startListening({
  actionCreator: occupyChair.fulfilled,
  effect: async (action, listenerApi) => {
    const state = listenerApi.getState();
    const { selectedSpeaker } = state.mediaControls;
    if (action?.payload?.message === 'SUCCESS') {
      try {
        const audio = new Audio('placement.wav');
        if (selectedSpeaker?.deviceId) {
          await audio.setSinkId(selectedSpeaker.deviceId);
        }
        await audio.play();
      } catch (error) {
        console.error('Audio playback error:', error);
      }
    } else {
      listenerApi.dispatch(addNotification(
        NotificationBuilder.error()
          .withHeader('occupation_failed', true)
          .withMessage('occupation_failed_msg', true)
          .withLucideIcon('ban')
          .build()
      ));
    }
  },
});

notificationMiddleware.startListening({
  actionCreator: occupyChair.rejected,
  effect: async (action, listenerApi) => {
    if (action?.payload === 'ROOM_LOCKED') {
      listenerApi.dispatch(addNotification(
        NotificationBuilder.error()
          .withHeader('room_locked', true)
          .withMessage('room_locked_cant_join_msg', true)
          .withLucideIcon('lock')
          .build()
      ));
    } else if (action.payload === 'CONFERENCE_IN_PROGRESS') {
      listenerApi.dispatch(addNotification(
        NotificationBuilder.error()
          .withHeader('conference_in_progress', true)
          .withMessage('conference_in_progress_msg', true)
          .build()
      ));
    }
  },
});

notificationMiddleware.startListening({
  actionCreator: transcriptionStarted,
  effect: async (_, listenerApi) => {
    listenerApi.dispatch(addNotification(
      NotificationBuilder.info()
        .withHeader('transcription_enabled', true)
        .withMessage('transcription_enabled_msg', true)
        .build()
    ));
  },
});

notificationMiddleware.startListening({
  actionCreator: transcriptionStopped,
  effect: async (_, listenerApi) => {
    listenerApi.dispatch(addNotification(
      NotificationBuilder.info()
        .withHeader('transcription_disabled', true)
        .withMessage('transcription_disabled_msg', true)
        .build()
    ));
  },
});
const lastWebPushBySender = new Map();
const WEB_PUSH_COOLDOWN = 30 * 1000; 
notificationMiddleware.startListening({
  actionCreator: RECIEVE_MESSAGE,
  effect: async (action, listenerApi) => {
    try {
      const { sender, message, spark_transaction_id, encouragement_id, amount, unread_count } = action.payload.data;
      const senderName =  sender?.display_name || sender?.firstName + sender?.lastName ||  sender?.name || 'someone';
      const user = {
        id: sender.id,
        lastName: sender?.lastName || sender?.display_name ,
        firstName: sender?.firstName || '',
        urlphoto: sender.urlphoto || '',
        is_online: sender.is_online,
      };

      const notificationMessage = spark_transaction_id
        ? {
          text: "sparks_received_message",
          translate: true,
          variables: { name: senderName, amount, message: message ? '\n' + message : "" }
        }
        : encouragement_id
          ? {
            text: "encouragement_received_message",
            translate: true,
            variables: { name: senderName }
          }
          : {
            text: message,
            translate: false,
            variables: {}
          };
      
      const conversation = { id: `dm_${sender.id}`, unread_count };
      listenerApi.dispatch(addNotification(
        NotificationBuilder.message(user, conversation)
          .withMessage(notificationMessage.text, notificationMessage.translate, notificationMessage.variables)
          .withSound('iphone_message.mp3')
          .build()
      ));
      const now = Date.now();
      const lastPushTime = lastWebPushBySender.get(sender.id);
      // Push notification: pre-translated system copy (title) + the raw
      // message content (not a translation key, so no i18n.t needed for it).
      const pushTitle = i18n.t('new_direct_message');
      const pushMessage = notificationMessage.translate ? i18n.t(notificationMessage.text, notificationMessage.variables) : notificationMessage.text;
         if (!lastPushTime || now - lastPushTime >= WEB_PUSH_COOLDOWN) {
        const pushTitle = i18n.t("new_direct_message");
        const pushMessage = notificationMessage.translate
          ? i18n.t(notificationMessage.text, notificationMessage.variables)
          : notificationMessage.text;

        sendWebPushNotification(pushTitle, pushMessage);

        lastWebPushBySender.set(sender.id, now);
      }
    } catch (err) {
      console.error("error receiving message notification", err);
    }
  },
});

notificationMiddleware.startListening({
  actionCreator: RECEIVED_ROOM_MESSAGE,
  effect: async (action, listenerApi) => {
    const { sender, roomLabel, message } = action.payload.data;
    const senderName = sender?.display_name || sender?.name || 'someone';
    const user = {
      id: sender.id,
      username: senderName,
      urlphoto: sender.urlphoto || '',
      is_online: true,
    };
    const roomId = action.payload.data.roomId || roomLabel;
    const conversation = { id: roomId || 'room_placeholder' };

    listenerApi.dispatch(addNotification(
      NotificationBuilder.message(user, conversation)
        .withMessage(message, false)
        .withOptions({ roomLabel })
        .build()
    ));

    const pushTitle = i18n.t('new_room_message_in', { room: roomLabel });
    sendWebPushNotification(pushTitle, message);
  },
});

notificationMiddleware.startListening({
  actionCreator: deleteDefaultBackground.fulfilled,
  effect: async (_, listenerApi) => {
    listenerApi.dispatch(addNotification(
      NotificationBuilder.success()
        .withHeader('background_deleted', true)
        .withMessage('background_deleted_msg', true)
        .build()
    ));
  },
});

notificationMiddleware.startListening({
  actionCreator: deleteDefaultBackground.rejected,
  effect: async (_, listenerApi) => {
    listenerApi.dispatch(addNotification(
      NotificationBuilder.error()
        .withHeader('background_delete_error', true)
        .withMessage('background_delete_error_msg', true)
        .build()
    ));
  },
});

notificationMiddleware.startListening({
  actionCreator: virerUser.fulfilled,
  effect: async (action, listenerApi) => {
    const name = action.payload.user;
    listenerApi.dispatch(addNotification(
      NotificationBuilder.success()
        .withHeader('user_excluded', true)
        .withMessage('user_excluded_msg', true, { name })
        .withLucideIcon('user-round-minus')
        .build()
    ));
  },
});

notificationMiddleware.startListening({
  actionCreator: virerUser.rejected,
  effect: async (_, listenerApi) => {
    listenerApi.dispatch(addNotification(
      NotificationBuilder.error()
        .withHeader('exclusion_failed', true)
        .withMessage('exclusion_failed_msg', true)
        .build()
    ));
  },
});

notificationMiddleware.startListening({
  actionCreator: ON_EXCLUDE_USER,
  effect: async (action, listenerApi) => {
    const sender = action.payload.data.sender;
    listenerApi.dispatch(addNotification(
      NotificationBuilder.info()
        .withHeader('excluded_by_user', true)
        .withMessage('excluded_by_user_msg', true, { name: sender })
        .withLucideIcon('log-out')
        .build()
    ));
  },
});

notificationMiddleware.startListening({
  actionCreator: uploadDefaultBackground.rejected,
  effect: async (_, listenerApi) => {
    listenerApi.dispatch(addNotification(
      NotificationBuilder.error()
        .withHeader('upload_failed', true)
        .withMessage('upload_failed_msg', true)
        .withLucideIcon('cloud-off')
        .build()
    ));
  },
});

notificationMiddleware.startListening({
  actionCreator: uploadDefaultBackground.fulfilled,
  effect: async (_, listenerApi) => {
    listenerApi.dispatch(addNotification(
      NotificationBuilder.success()
        .withHeader('background_uploaded', true)
        .withMessage('background_uploaded_msg', true)
        .build()
    ));
  },
});

notificationMiddleware.startListening({
  actionCreator: uploadBackground.rejected,
  effect: async (_, listenerApi) => {
    listenerApi.dispatch(addNotification(
      NotificationBuilder.error()
        .withHeader('upload_failed', true)
        .withMessage('upload_failed_msg', true)
        .withLucideIcon('cloud-off')
        .build()
    ));
  },
});

notificationMiddleware.startListening({
  actionCreator: uploadBackground.fulfilled,
  effect: async (_, listenerApi) => {
    listenerApi.dispatch(addNotification(
      NotificationBuilder.success()
        .withHeader('background_uploaded', true)
        .withMessage('background_uploaded_msg', true)
        .build()
    ));
  },
});

notificationMiddleware.startListening({
  actionCreator: updatePhoto.fulfilled,
  effect: async (_, listenerApi) => {
    listenerApi.dispatch(addNotification(
      NotificationBuilder.success()
        .withHeader('photo_updated', true)
        .withMessage('photo_updated_msg', true)
        .withLucideIcon('user-round-check')
        .build()
    ));
  },
});

notificationMiddleware.startListening({
  actionCreator: apiThunkForSupportModal.fulfilled,
  effect: async (_, listenerApi) => {
    listenerApi.dispatch(addNotification(
      NotificationBuilder.success()
        .withHeader('support_request_sent', true)
        .withMessage('support_request_sent_msg', true)
        .build()
    ));
  },
});

notificationMiddleware.startListening({
  actionCreator: apiThunkForSupportModal.rejected,
  effect: async (_, listenerApi) => {
    // NOTE: original had severity: "success" on this rejection branch —
    // another copy/paste bug, corrected to .error() here. Flagged
    // explicitly rather than silently carried through, per above.
    listenerApi.dispatch(addNotification(
      NotificationBuilder.error()
        .withHeader('error_submission', true)
        .withMessage('error_submission_msg', true)
        .build()
    ));
  },
});

notificationMiddleware.startListening({
  actionCreator: deletePhoto.fulfilled,
  effect: async (_, listenerApi) => {
    listenerApi.dispatch(addNotification(
      NotificationBuilder.success()
        .withHeader('photo_deleted', true)
        .withMessage('photo_deleted_msg', true)
        .build()
    ));
  },
});

notificationMiddleware.startListening({
  actionCreator: deletePhoto.rejected,
  effect: async (_, listenerApi) => {
    listenerApi.dispatch(addNotification(
      NotificationBuilder.error()
        .withHeader('photo_delete_error', true)
        .withMessage('photo_delete_error_msg', true)
        .build()
    ));
  },
});

notificationMiddleware.startListening({
  actionCreator: copyRoomLink,
  effect: async (_, listenerApi) => {
    listenerApi.dispatch(addNotification(
      NotificationBuilder.info()
        .withHeader('link_copied', true)
        .withMessage('link_copied_msg', true)
        .withLucideIcon('clipboard-check')
        .build()
    ));
  },
});
